import React from 'react';
import { Box, Grid, Paper, Typography, TextField, InputAdornment, Container } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SecurityIcon from '@mui/icons-material/Security';
import BugReportIcon from '@mui/icons-material/BugReport';
import TokenIcon from '@mui/icons-material/Token';
import ShieldIcon from '@mui/icons-material/Shield';
import VulnerabilityChart from '../components/VulnerabilityChart';

// Background glowing effect component
const GlowingBackground = () => (
  <Box
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
      background: 'linear-gradient(135deg, #0A061D 0%, #13102B 100%)',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '10%',
        left: '5%',
        width: '30%',
        height: '30%',
        background: 'radial-gradient(circle, rgba(123, 97, 255, 0.15) 0%, transparent 70%)',
        filter: 'blur(45px)',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '10%',
        right: '5%',
        width: '35%',
        height: '35%',
        background: 'radial-gradient(circle, rgba(33, 203, 243, 0.12) 0%, transparent 70%)',
        filter: 'blur(45px)',
      }
    }}
  />
);

// Stats Card component with glow effect
const StatCard = ({ icon, title, value }: { icon: React.ReactNode, title: string, value: string }) => (
  <Paper
    sx={{
      p: 3,
      background: 'rgba(123, 97, 255, 0.05)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(123, 97, 255, 0.1)',
      borderRadius: 2,
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 8px 30px rgba(123, 97, 255, 0.2)',
        '&::after': {
          transform: 'rotate(45deg) translate(100%, -100%)',
        }
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '200%',
        height: '200%',
        background: 'linear-gradient(45deg, transparent, rgba(123, 97, 255, 0.1), transparent)',
        transition: 'transform 0.5s ease',
        transform: 'rotate(45deg) translate(-100%, 100%)',
      }
    }}
  >
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      {icon}
      <Typography variant="h3" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {title}
      </Typography>
    </Box>
  </Paper>
);

// Action button component
const ActionButton = ({ text }: { text: string }) => (
  <Paper
    sx={{
      px: 3,
      py: 1.5,
      background: 'rgba(123, 97, 255, 0.05)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(123, 97, 255, 0.1)',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      '&:hover': {
        background: 'linear-gradient(45deg, rgba(123, 97, 255, 0.1), rgba(33, 203, 243, 0.1))',
        transform: 'translateY(-2px)',
        boxShadow: '0 5px 15px rgba(123, 97, 255, 0.15)',
      }
    }}
  >
    <Typography sx={{ color: 'text.secondary' }}>{text}</Typography>
  </Paper>
);

const NewDashboard: React.FC = () => (
  <Box sx={{ position: 'relative', minHeight: '100vh' }}>
    <GlowingBackground />
    
    <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, py: 4 }}>
      {/* Hero Section */}
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '2rem', md: '3.5rem' },
            fontWeight: 700,
            mb: 2,
            background: 'linear-gradient(45deg, #FFFFFF, #7B61FF)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Scan any smart contract, token or NFT
        </Typography>
        
        <Typography
          variant="h5"
          sx={{
            color: 'text.secondary',
            mb: 4,
            maxWidth: '800px',
            mx: 'auto',
          }}
        >
          Find out about threats and vulnerabilities with our in-depth analysis before you invest.
        </Typography>
        
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search project, token name or contract address"
          sx={{
            maxWidth: '800px',
            '& .MuiOutlinedInput-root': {
              height: '60px',
              background: 'rgba(123, 97, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              '&:hover': {
                background: 'rgba(123, 97, 255, 0.08)',
              },
              '& fieldset': {
                borderColor: 'rgba(123, 97, 255, 0.2)',
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#7B61FF' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        {[
          { icon: <SecurityIcon sx={{ color: '#7B61FF', fontSize: 48 }} />, title: 'Total Contracts Scanned', value: '37' },
          { icon: <BugReportIcon sx={{ color: '#21CBF3', fontSize: 48 }} />, title: 'Total Issues Found', value: '67' },
          { icon: <TokenIcon sx={{ color: '#FF61D3', fontSize: 48 }} />, title: 'Risk Tokens Detected', value: '12' },
          { icon: <ShieldIcon sx={{ color: '#21CBF3', fontSize: 48 }} />, title: 'Exploits in Crypto', value: '828' },
        ].map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid>

      {/* Vulnerability Chart Section */}
      <Paper
        sx={{
          p: 4,
          mb: 6,
          background: 'rgba(123, 97, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(123, 97, 255, 0.1)',
          borderRadius: 2,
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(123, 97, 255, 0.2)',
            '&::after': {
              transform: 'rotate(45deg) translate(100%, -100%)',
            }
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '200%',
            height: '200%',
            background: 'linear-gradient(45deg, transparent, rgba(123, 97, 255, 0.1), transparent)',
            transition: 'transform 0.5s ease',
            transform: 'rotate(45deg) translate(-100%, 100%)',
          }
        }}
      >
        <Typography
          variant="h2"
          sx={{
            mb: 3,
            fontSize: '2rem',
            fontWeight: 600,
            background: 'linear-gradient(45deg, #FFFFFF, #7B61FF)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Vulnerability Distribution
        </Typography>
        <Box 
          sx={{ 
            height: '400px', 
            position: 'relative', 
            zIndex: 1,
            width: '100%',
            '& .recharts-responsive-container': {
              minHeight: '360px'
            }
          }}
        >
          <VulnerabilityChart />
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Grid container spacing={2}>
        {['Most Scanned', 'Recent Threats', 'By Market Cup', 'Trending', 'Advanced Search'].map((text) => (
          <Grid item key={text}>
            <ActionButton text={text} />
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

export default NewDashboard;